<template>
  <div
    :id="componentId"
    class="w-100 px-0 d-flex flex-row justify-content-between align-items-center mb-space-xs"
    role="group"
    :aria-labelledby="`${componentId}-title`"
  >
    <h3 class="text-left" :class="titleClass" :id="`${componentId}-title`">
      {{ title }}
    </h3>
  </div>
  <div
    class="form-check mb-space-xxs"
    v-for="option in options"
    :key="option.value"
  >
    <input
      class="form-check-input"
      type="checkbox"
      :checked="selected?.has(option.value)"
      @click="handleChecked(option)"
      :id="`${componentId}-${option.value}`"
      :aria-checked="selected?.has(option.value)"
      :aria-labelledby="`${componentId}-${option.value}-label`"
    />
    <label
      class="form-check-label d-inline-flex align-items-center fs-small"
      :for="`${componentId}-${option.value}`"
      :id="`${componentId}-${option.value}-label`"
    >
      {{ option?.text }}
      <ToolTip v-if="showTooltip && option.description">
        <template v-slot:tooltip>
          <div v-html="option.description"></div>
        </template>
        <font-awesome-icon
          class="info-icon text-primary"
          icon="info-circle"
        ></font-awesome-icon>
      </ToolTip>
    </label>
  </div>
</template>

<script setup lang="ts">
import ToolTip from "~/components/ToolTip.vue";
import { TitleVariant, VariantOption } from "~/types/types";
export type FilterOption = {
  value: string;
  text: string;
  description?: string;
};

type Props = {
  componentId: string;
  title: string;
  options: FilterOption[];
  showTooltip?: boolean;
  titleSize?: TitleVariant;
  titleVariant?: VariantOption;
};
const props = withDefaults(defineProps<Props>(), {
  showTooltip: false,
  titleSize: "small",
  titleVariant: "dark-3",
});

/*
 ********************************************
 *  Emits
 ********************************************
 */
const emits = defineEmits<{
  (e: "checked", value: FilterOption): void;
  (e: "unchecked", value: FilterOption): void;
  (e: "change", value: FilterOption): void;
}>();
/*
 ********************************************
 *  data
 ********************************************
 */
const selected = defineModel<Set<string>>({ required: true });
/*
 ********************************************
 * computed
 ********************************************
 */
const titleClass = computed(() => {
  return `h3-${props.titleSize} fs-${props.titleVariant}`;
});
/*
 ********************************************
 *  methods
 ********************************************
 */
const handleChecked = (option: FilterOption) => {
  if (selected.value?.has(option.value)) {
    selected.value.delete(option.value);
    emits("unchecked", option);
  } else {
    selected.value?.add(option.value);
    emits("checked", option);
  }
  emits("change", option);
};
</script>

<style scoped></style>
